import React, { ReactNode, useEffect } from 'react';
import './course.css';
import './buttons.css';

import { CourseContent } from '../types';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLessonProgress } from '../../../hooks/useLessonProgress';

const YouTubePlayer: React.FC<{ videoId: string }> = ({ videoId }) => {
  return (
    <div className="video-container" style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
      <iframe
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        src={`https://www.youtube.com/embed/${videoId}?si=Lco4KUkiVb2Tf__h`}
        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
  );
};

export const CourseLayout: React.FC<{
  content: CourseContent;
  right?: ReactNode;
  onNext?: () => void;
}> = ({ content, right, onNext }) => {
  const { darkMode } = useTheme();
  const {
    progress,
    toggleWatched,
    updateLike
  } = useLessonProgress(content.itemId);

  const handleMarcarComoVisto = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await toggleWatched();
    } catch (error) {
      console.error('Erro ao marcar como visto:', error);
      if (error instanceof Error) {
        alert(error.message);
      }
    }
  };

  const handleGostei = async (e: React.MouseEvent, liked: boolean | null) => {
    e.preventDefault();
    try {
      await updateLike(progress.liked === liked ? null : liked);
    } catch (error) {
      console.error('Erro ao registrar avaliação:', error);
      if (error instanceof Error) {
        alert(error.message);
      }
    }
  };

  const handleNext = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (onNext) {
      // Marca como visto automaticamente ao avançar
      try {
        if (!progress.watched) {
          await toggleWatched();
        }
      } catch (error) {
        console.error('Erro ao marcar como visto:', error);
      }
      onNext();
    }
  };

  // Sync body dark-mode and vitrine dark stylesheet for this page
  useEffect(() => {
    const id = 'dark-styles';
    if (darkMode) {
      document.body.classList.add('dark-mode');
      if (!document.getElementById(id)) {
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = '/vitrine_assets/styles-dark.css';
        document.head.appendChild(link);
      }
    } else {
      document.body.classList.remove('dark-mode');
      const darkLink = document.getElementById(id);
      if (darkLink) darkLink.remove();
    }
  }, [darkMode]);

  return (
    <div className={`content-wrapper center ${darkMode ? 'dark-mode' : ''}`}>
      <div className="container">
        <div className="row item">
          <div className="col-lg-9 mx-auto order-1">
            {/* Header removed: breadcrumb-title and breadcrumb intentionally omitted per request */}
            {/* Re-add lightweight header with breadcrumbs and small back link */}
            <div className="breadcrumb-title d-flex align-items-center justify-content-between py-2">
              <div>
                <a href="/vitrine" className="btn btn-sm btn-outline back-to-vitrine" style={{marginRight:12}}>Início</a>
              </div>

              {/* Centered course title (removed full breadcrumb path) */}
              <div className="course-title-wrapper">
                <span className="course-title" style={{fontWeight:700}}>{content.title}</span>
              </div>

              {/* placeholder for symmetry / spacing on the right */}
              <div style={{width:48}} aria-hidden />
            </div>

            <div className="class item-pos my-5">
              <YouTubePlayer videoId={content.video.videoId} />
            </div>

            <article>
              {content.article.map((p, idx) => {
                if (typeof p === 'string') {
                  // Verifica se a string contém HTML
                  if (p.includes('<') && p.includes('>')) {
                    return <div key={idx} className="article-paragraph" dangerouslySetInnerHTML={{ __html: p }} />;
                  }
                  return <div key={idx} className="article-paragraph">{p}</div>;
                } else {
                  return <div key={idx} className="article-paragraph" dangerouslySetInnerHTML={{ __html: p.html }} />;
                }
              })}
            </article>

            <hr className="mt-5" />
            <div className="class-control py-5">
              <div className="row">
                <div className="col-12 col-sm flex-sm-grow-0 text-center text-sm-left text-nowrap avaliacao">
                  <button type="button"
                    className={`btn btn-bull btn-like ${progress.liked === true ? 'active' : ''}`}
                    onClick={(e) => handleGostei(e, true)}
                  >
                    <svg className="icon icon-thumbs-up"><use xlinkHref="#icon-thumbs-up" /></svg>
                  </button>
                  <button type="button"
                    className={`btn btn-bull ml-2 btn-like ${progress.liked === false ? 'active' : ''}`}
                    onClick={(e) => handleGostei(e, false)}
                  >
                    <svg className="icon icon-thumbs-down"><use xlinkHref="#icon-thumbs-down" /></svg>
                  </button>
                  <button type="button"
                    className={`btn btn-bull btn-bull-expanded progresso-click ml-4 ${progress.watched ? 'active' : ''}`}
                    onClick={handleMarcarComoVisto}
                  >
                    <span className="pr-2">Marcar como visto</span>
                    <svg className="icon icon-simple-check"><use xlinkHref="#icon-simple-check" /></svg>
                  </button>
                </div>
                {onNext && (
                  <div className="col-12 col-sm flex-sm-grow-1 text-center text-sm-right pt-4 pt-sm-0">
                    <button type="button"
                      className="btn btn-bull btn-bull-expanded ml-2 btn-proximo"
                      onClick={handleNext}
                    >
                      <span className="pr-2">Próximo</span>
                      <svg className="icon icon-arrow-right"><use xlinkHref="#icon-arrow-right" /></svg>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* footer area cleaned: heart and marketing text removed */}
          </div>
          <div className="col-lg-3 order-2">
            {right}
          </div>
        </div>
      </div>
    </div>
  );
};

